import { Injectable } from '@angular/core';
import {
    ISuperTableCalculation,
    ISuperTableCalculationFunction,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { AumicoStyleOptions, ICellFormat } from '@modeso/types__aumico-lib-core-be';
import { IXAxisConfigColumn, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import {
    FIRST_ELEMENT_INDEX,
    NumberUtils,
    SECOND_ELEMENT_INDEX,
    TWO_DECIMAL_PLACES,
} from '../../../../aumico-lib-core-fe/src/lib';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { ISuperTableComponentFormValue } from '../interfaces/super-table-component-form-value.interface';
import { ISuperTableComponentRowFormValue } from '../interfaces/super-table-component-row-form-value.interface';

@Injectable({ providedIn: 'root' })
export class SuperTableHelper {
    /** FX (REV-21512): the display-currency twin column types — metadata-only, values computed. */
    public static isFxColumnType(type: SuperTableColumnType | string | undefined): boolean {
        return type === SuperTableColumnType.CURRENT_YEAR_FX || type === SuperTableColumnType.PREVIOUS_YEAR_FX;
    }

    /** FX (REV-21512): rows whose twin cells hold user input instead of computed values. */
    public static isFxUserValueRowType(type: SuperTableRowType | string | undefined): boolean {
        return (
            type === SuperTableRowType.FREE_TEXT ||
            type === SuperTableRowType.TITLE ||
            type === SuperTableRowType.TEXT_ROW
        );
    }

    /**
     * FX (REV-21512): calculation rows over the display-currency column — the same four functions
     * the functional column runs, fed by `fxValueOf` (a finite number participates, `undefined`
     * is ignored exactly like a non-numeric functional cell, and `null` — a twin that cannot be
     * derived because rates are missing — empties the result instead of printing a wrong number).
     * Rows are resolved in order, so a calculation over an earlier calculation works like it does
     * for the functional columns.
     */
    computeFxCalculationRowValues(
        rows: Array<{ rowId?: string; type?: SuperTableRowType | string }>,
        calculations: ISuperTableCalculation[] | undefined,
        fxValueOf: (row: { rowId?: string }, year: 'cy' | 'py') => number | null | undefined,
    ): Map<string, { cy: number | null; py: number | null }> {
        const results = new Map<string, { cy: number | null; py: number | null }>();
        const resolve = (row: { rowId?: string } | undefined, year: 'cy' | 'py'): number | null | undefined => {
            if (row == null) {
                return undefined;
            }
            const computed = row.rowId != null ? results.get(row.rowId) : undefined;

            return computed != null ? computed[year] : fxValueOf(row, year);
        };
        const sumOf = (operands: Array<number | null | undefined>): number | null =>
            operands.some((value) => value === null)
                ? null
                : NumberUtils.sumIgnoringInfiniteNumbers(operands.map((value) => value ?? NaN) as number[], 0);

        for (const row of rows) {
            if (row.type !== SuperTableRowType.CALCULATION || row.rowId == null) {
                continue;
            }
            const calculation = calculations?.find((calc) => calc.calcId === row.rowId);
            if (calculation == null) {
                continue;
            }
            const operandRows = (calculation.rowsIds ?? []).map((rowId) =>
                rows.find((candidate) => candidate.rowId === rowId),
            );
            const yearValue = (year: 'cy' | 'py'): number | null => {
                const operands = operandRows.map((operandRow) => resolve(operandRow, year));
                switch (calculation.function) {
                    case ISuperTableCalculationFunction.SUMMATION:
                        return sumOf(operands);
                    case ISuperTableCalculationFunction.SUBTRACTION: {
                        const [minuend, subtrahend] = [operands[FIRST_ELEMENT_INDEX], operands[SECOND_ELEMENT_INDEX]];
                        if (minuend === null || subtrahend === null) {
                            return null;
                        }

                        return NumberUtils.subtractNumbers(
                            NumberUtils.isFinite(minuend) ? (minuend as number) : 0,
                            NumberUtils.isFinite(subtrahend) ? (subtrahend as number) : 0,
                        );
                    }
                    case ISuperTableCalculationFunction.RATIO: {
                        const summation = sumOf(operands);
                        const relationRow = rows.find((candidate) => candidate.rowId === calculation.relationItemId);
                        const relation = resolve(relationRow, year);
                        if (summation === null || relation === null) {
                            return null;
                        }

                        return NumberUtils.calculatePercentage(summation, relation || 1);
                    }
                    case ISuperTableCalculationFunction.MULTIPLICATION: {
                        if (operands.some((value) => value === null)) {
                            return null;
                        }
                        const factors = operands.map((value) => value ?? NaN) as number[];
                        const product = NumberUtils.multiplyNumbers(
                            factors.length >= 2 ? factors : [factors[FIRST_ELEMENT_INDEX], 0],
                        );

                        return isNaN(product) ? 0 : product;
                    }
                    default:
                        return null;
                }
            };
            results.set(row.rowId, { cy: yearValue('cy'), py: yearValue('py') });
        }

        return results;
    }

    getSummationRow(
        calculation: ISuperTableCalculation,
        formValue: ISuperTableComponentFormValue,
        xAxisConfigCols: IXAxisConfigColumn[],
        compareYears: Boolean,
    ): ISuperTableComponentRowFormValue {
        const rowSelections = formValue.rows.filter((row) => calculation.rowsIds.includes(row.rowId));
        const itemsCurrentValues = rowSelections.map((item) => item.currentValue);
        const currentValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsCurrentValues, 0);

        const calculationRowInForm = formValue.rows.find((row) => row.rowId === calculation.calcId);
        const summationRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: calculationRowInForm != null ? calculationRowInForm.title : calculation.title,
            type: SuperTableRowType.CALCULATION,
            itemsIds: [],
            currentValue: currentValuesSummation,
            format:
                calculationRowInForm != null
                    ? calculationRowInForm.format
                    : {
                        styles: [AumicoStyleOptions.BOLD],
                        numberOfDecimals: TWO_DECIMAL_PLACES,
                    },
        };

        if (compareYears) {
            const itemsPreviousValues = rowSelections.map((item) => item.previousValue);
            const previousValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsPreviousValues, 0);
            summationRow.previousValue = previousValuesSummation;
        }

        for (const col of xAxisConfigCols) {
            if (col.type === SuperTableColumnType.CURRENT_YEAR || col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                continue;
            }

            if (col.type === SuperTableColumnType.TEXT) {
                // Preserve existing text value if it exists, otherwise use empty string
                summationRow[col.columnId] = calculationRowInForm?.[col.columnId] || '';
            }

            if (col.type === SuperTableColumnType.NUMBER || col.type === SuperTableColumnType.CALCULATION) {
                const itemsColValues: number[] = rowSelections.map((item) => +item[col.columnId]);
                const itemsColValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsColValues, 0);
                summationRow[col.columnId] = itemsColValuesSummation;
            }
        }

        return summationRow;
    }

    getRatioRow(
        calculation: ISuperTableCalculation,
        formValue: ISuperTableComponentFormValue,
        xAxisConfigCols: IXAxisConfigColumn[],
        compareYears: Boolean,
    ): ISuperTableComponentRowFormValue {
        const rowSelections = formValue.rows.filter((row) => calculation.rowsIds.includes(row.rowId));
        const relationRow = formValue.rows.find((row) => row.rowId === calculation.relationItemId);
        let currentValuesRatio: number;

        let previousValuesRatio: number;

        const calculationRowInForm = formValue.rows.find((row) => row.rowId === calculation.calcId);
        const ratioRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: calculationRowInForm != null ? calculationRowInForm.title : calculation.title,
            type: SuperTableRowType.CALCULATION,
            itemsIds: [],
            format:
                calculationRowInForm != null
                    ? calculationRowInForm.format
                    : {
                        styles: [AumicoStyleOptions.BOLD],
                        numberOfDecimals: TWO_DECIMAL_PLACES,
                    },
        };

        for (const col of xAxisConfigCols) {
            if (col.type === SuperTableColumnType.CURRENT_YEAR) {
                const itemsCurrentValues = rowSelections.map((item) => item.currentValue);
                const currentValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsCurrentValues, 0);

                currentValuesRatio = NumberUtils.calculatePercentage(currentValuesSummation, relationRow?.currentValue || 1);
                ratioRow.currentValue = currentValuesRatio;
                continue;
            }

            if (compareYears && col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                const itemsPreviousValues = rowSelections.map((item) => item.previousValue);
                const previousValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsPreviousValues, 0);
                previousValuesRatio = NumberUtils.calculatePercentage(
                    previousValuesSummation,
                    relationRow?.previousValue || 1,
                );

                ratioRow.previousValue = previousValuesRatio;
                continue;
            }

            if (col.type === SuperTableColumnType.TEXT) {
                // Preserve existing text value if it exists, otherwise use empty string
                ratioRow[col.columnId] = calculationRowInForm?.[col.columnId] || '';
                continue;
            }

            if (col.type === SuperTableColumnType.NUMBER || col.type === SuperTableColumnType.CALCULATION) {
                const itemsColValues: number[] = rowSelections.map((item) => +item[col.columnId]);
                const itemsColValuesSummation = NumberUtils.sumIgnoringInfiniteNumbers(itemsColValues, 0);
                const itemsColValuesRatio = NumberUtils.calculatePercentage(
                    itemsColValuesSummation,
                    (relationRow?.[col.columnId] as number) ?? 1,
                );
                ratioRow[col.columnId] = itemsColValuesRatio;
                continue;
            }
        }

        return ratioRow;
    }

    getSubtractionRow(
        calculation: ISuperTableCalculation,
        formValue: ISuperTableComponentFormValue,
        xAxisConfigCols: IXAxisConfigColumn[],
        compareYears: Boolean,
    ): ISuperTableComponentRowFormValue {
        const minuendSubtractionRow = formValue.rows.find(
            (row) => calculation.rowsIds[FIRST_ELEMENT_INDEX] === row.rowId,
        );
        const subtrahendSubtractionRow = formValue.rows.find(
            (row) => calculation.rowsIds[SECOND_ELEMENT_INDEX] === row.rowId,
        );

        const currentValuesDifference = NumberUtils.subtractNumbers(
            minuendSubtractionRow && NumberUtils.isFinite(minuendSubtractionRow.currentValue) ? minuendSubtractionRow.currentValue : 0,
            subtrahendSubtractionRow && NumberUtils.isFinite(subtrahendSubtractionRow.currentValue) ? subtrahendSubtractionRow.currentValue : 0,
        );

        const calculationRowInForm = formValue.rows.find((row) => row.rowId === calculation.calcId);
        const subtractionRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: calculationRowInForm != null ? calculationRowInForm.title : calculation.title,
            type: SuperTableRowType.CALCULATION,
            itemsIds: [],
            currentValue: currentValuesDifference,
            format:
                calculationRowInForm != null
                    ? calculationRowInForm.format
                    : {
                        styles: [AumicoStyleOptions.BOLD],
                        numberOfDecimals: TWO_DECIMAL_PLACES,
                    },
        };

        if (compareYears) {
            const previousValuesDifference = NumberUtils.subtractNumbers(
                minuendSubtractionRow && NumberUtils.isFinite(minuendSubtractionRow.previousValue)
                    ? minuendSubtractionRow.previousValue
                    : 0,
                subtrahendSubtractionRow && NumberUtils.isFinite(subtrahendSubtractionRow.previousValue)
                    ? subtrahendSubtractionRow.previousValue
                    : 0,
            );

            subtractionRow.previousValue = previousValuesDifference;
        }

        for (const col of xAxisConfigCols) {
            if (col.type === SuperTableColumnType.CURRENT_YEAR || col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                continue;
            }

            if (col.type === SuperTableColumnType.TEXT) {
                // Preserve existing text value if it exists, otherwise use empty string
                subtractionRow[col.columnId] = calculationRowInForm?.[col.columnId] || '';
            }

            if (col.type === SuperTableColumnType.NUMBER || col.type === SuperTableColumnType.CALCULATION) {
                const itemsColValuesDifference = NumberUtils.subtractNumbers(
                    minuendSubtractionRow?.[col.columnId] ? +minuendSubtractionRow[col.columnId] : 0,
                    subtrahendSubtractionRow?.[col.columnId] ? +subtrahendSubtractionRow[col.columnId] : 0,
                );
                subtractionRow[col.columnId] = itemsColValuesDifference;
            }
        }

        return subtractionRow;
    }

    getMultiplicationRow(
        calculation: ISuperTableCalculation,
        formValue: ISuperTableComponentFormValue,
        xAxisConfigCols: IXAxisConfigColumn[],
        compareYears: Boolean,
    ): ISuperTableComponentRowFormValue {
        const rowSelections = formValue.rows.filter((row) => calculation.rowsIds.includes(row.rowId));
        const itemsCurrentValues = rowSelections.map((item) => item.currentValue);
        const currentValuesMultiplication = NumberUtils.multiplyNumbers(
            itemsCurrentValues.length >= 2 ? itemsCurrentValues : [itemsCurrentValues[FIRST_ELEMENT_INDEX], 0],
        );

        const calculationRowInForm = formValue.rows.find((row) => row.rowId === calculation.calcId);
        const multiplicationRow: ISuperTableComponentRowFormValue = {
            rowId: undefined,
            title: calculationRowInForm != null ? calculationRowInForm.title : calculation.title,
            type: SuperTableRowType.CALCULATION,
            itemsIds: [],
            currentValue: currentValuesMultiplication,
            format:
                calculationRowInForm != null
                    ? calculationRowInForm.format
                    : {
                        styles: [AumicoStyleOptions.BOLD],
                        numberOfDecimals: TWO_DECIMAL_PLACES,
                    },
        };

        if (compareYears) {
            const itemsPreviousValues = rowSelections.map((item) => item.previousValue);
            const previousValuesMultiplication = NumberUtils.multiplyNumbers(
                itemsPreviousValues.length >= 2 ? itemsPreviousValues : [itemsPreviousValues[FIRST_ELEMENT_INDEX], 0],
            );
            multiplicationRow.previousValue = isNaN(previousValuesMultiplication) ? 0 : previousValuesMultiplication;
        }

        for (const col of xAxisConfigCols) {
            if (col.type === SuperTableColumnType.CURRENT_YEAR || col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                continue;
            }

            if (col.type === SuperTableColumnType.TEXT) {
                // Preserve existing text value if it exists, otherwise use empty string
                multiplicationRow[col.columnId] = calculationRowInForm?.[col.columnId] || '';
            }

            if (col.type === SuperTableColumnType.NUMBER || col.type === SuperTableColumnType.CALCULATION) {
                const itemsColValues: number[] = rowSelections.map((item) => +item[col.columnId]);
                const itemsColValuesMultiplication = NumberUtils.multiplyNumbers(
                    itemsColValues.length >= 2 ? itemsColValues : [itemsColValues[FIRST_ELEMENT_INDEX], 0],
                );
                multiplicationRow[col.columnId] = isNaN(itemsColValuesMultiplication)
                    ? 0
                    : itemsColValuesMultiplication;
            }
        }

        return multiplicationRow;
    }

    public getReconcilationMisMatchResult(
        selectedReconcilationItems: MappingItemsSelectionNode[],
        superTableRow: ISuperTableComponentRowFormValue,
    ): {
        reconcilationCurrentYearMismatch: boolean;
        reconcilationPreviousYearMismatch: boolean;
    } {
        // REV-20899: reconciliation must compare against the "final" displayed
        // values — imported balance plus the transactional reclass aggregated
        // by `applyReclassificationsToFinStatements` /
        // `GroupingHelper.recalculateGroupSummaries`. Reading only
        // `calculatedClosingBalance` would mark a row as mismatched whenever
        // a reclass moved value onto / off the selected nodes.
        const finalCYValue = (node: MappingItemsSelectionNode): number => {
            const data = node?.data as
                | { calculatedClosingBalance?: number | null; closingReClass?: number | null }
                | undefined;
            if (data == null) return 0;
            const balance = data.calculatedClosingBalance ?? 0;
            const reclass = data.closingReClass ?? 0;
            return balance + reclass;
        };
        const finalPYValue = (node: MappingItemsSelectionNode): number => {
            const data = node?.data as
                | { calculatedOpeningBalance?: number | null; openingReClass?: number | null }
                | undefined;
            if (data == null) return 0;
            const balance = data.calculatedOpeningBalance ?? 0;
            const reclass = data.openingReClass ?? 0;
            return balance + reclass;
        };

        const closingValues = selectedReconcilationItems.map(finalCYValue);
        if (closingValues.length === 1) {
            closingValues.push(0);
        }
        const totalClosing = NumberUtils.sumNumbers(closingValues);

        const openingValues = selectedReconcilationItems.map(finalPYValue);
        if (openingValues.length === 1) {
            openingValues.push(0);
        }
        const totalOpening = NumberUtils.sumNumbers(openingValues);

        return {
            reconcilationCurrentYearMismatch: !NumberUtils.areNumbersEqual(totalClosing, superTableRow.currentValue),
            reconcilationPreviousYearMismatch: !NumberUtils.areNumbersEqual(totalOpening, superTableRow.previousValue),
        };
    }

    resolveEffectiveCellStyles(rowFormat?: ICellFormat, colFormat?: ICellFormat): AumicoStyleOptions[] {
        const resolvedStyles: AumicoStyleOptions[] = [];

        const rowStyles = rowFormat?.styles ?? {};
        const colStyles = colFormat?.styles ?? {};

        const alignmentStyles: AumicoStyleOptions[] = [
            AumicoStyleOptions.LEFT_ALIGNED,
            AumicoStyleOptions.CENTER_ALIGNED,
            AumicoStyleOptions.RIGHT_ALIGNED,
        ];

        // 🟡 Find alignment in row and column (only one expected per source)
        const rowAlignment = alignmentStyles.find((s) => rowStyles[s]?.enabled);
        const colAlignment = alignmentStyles.find((s) => colStyles[s]?.enabled);

        const rowTime = rowAlignment ? new Date(rowStyles[rowAlignment].appliedAt).getTime() : 0;
        const colTime = colAlignment ? new Date(colStyles[colAlignment].appliedAt).getTime() : 0;

        // ✅ Choose latest alignment
        if (rowAlignment && rowTime >= colTime) {
            resolvedStyles.push(rowAlignment);
        } else if (colAlignment) {
            resolvedStyles.push(colAlignment);
        }

        // 🟢 Now resolve all other styles normally
        const allStyleKeys = new Set<AumicoStyleOptions>([
            ...Object.keys(rowStyles),
            ...Object.keys(colStyles),
        ] as AumicoStyleOptions[]);

        for (const style of allStyleKeys) {
            if (alignmentStyles.includes(style)) continue; // skip alignment — already handled

            const rowEntry = rowStyles[style];
            const colEntry = colStyles[style];

            const rowAt = rowEntry?.appliedAt;
            const colAt = colEntry?.appliedAt;

            const rowTime = rowAt ? (rowAt instanceof Date ? rowAt.getTime() : new Date(rowAt).getTime()) : 0;
            const colTime = colAt ? (colAt instanceof Date ? colAt.getTime() : new Date(colAt).getTime()) : 0;

            const latestEntry = rowTime > colTime ? rowEntry : colEntry;

            if (latestEntry?.enabled) {
                resolvedStyles.push(style);
            }
        }

        return resolvedStyles;
    }

    resolveEffectiveDecimalPlaces(rowFormat?: ICellFormat, colFormat?: ICellFormat): number | undefined {
        if (rowFormat == null && colFormat == null) return undefined;

        if (rowFormat == null && colFormat != null && colFormat.numberOfDecimals != null) {
            return (colFormat.numberOfDecimals as { value: number; appliedAt?: Date }).value;
        }

        if (colFormat == null && rowFormat != null && rowFormat.numberOfDecimals != null) {
            return (rowFormat.numberOfDecimals as { value: number; appliedAt?: Date }).value;
        }

        const rowDecimals = rowFormat?.numberOfDecimals as { value: number; appliedAt?: Date } | undefined;
        const colDecimals = colFormat?.numberOfDecimals as { value: number; appliedAt?: Date } | undefined;

        if (rowDecimals == null && colDecimals == null) return undefined;

        if (
            (rowFormat.numberOfDecimals as { value: number; appliedAt?: Date })?.appliedAt == null &&
            colFormat.numberOfDecimals != null
        ) {
            return (colFormat.numberOfDecimals as { value: number; appliedAt?: Date }).value;
        }
        if (
            (colFormat.numberOfDecimals as { value: number; appliedAt?: Date })?.appliedAt == null &&
            rowFormat.numberOfDecimals != null
        ) {
            return (rowFormat.numberOfDecimals as { value: number; appliedAt?: Date }).value;
        }

        // 🛠 Normalize appliedAt to Date (in-place is safe if needed)
        if (rowDecimals?.appliedAt && typeof rowDecimals.appliedAt === 'string') {
            rowDecimals.appliedAt = new Date(rowDecimals.appliedAt);
        }
        if (colDecimals?.appliedAt && typeof colDecimals.appliedAt === 'string') {
            colDecimals.appliedAt = new Date(colDecimals.appliedAt);
        }

        return rowDecimals.appliedAt?.getTime() > colDecimals.appliedAt?.getTime()
            ? rowDecimals.value
            : colDecimals.value;
    }
}
